var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Button } from "../button/button.js";
import { attr } from "@microsoft/fast-element";
import { handleAttributeChange, toggleState } from "../../util/element-internals.js";
import { FilterPillEventNames, FilterPillSizes, FilterPillShapes, FilterPillAppearances } from "./filter-pill.options.js";
/**
 * FilterPill
 * @summary A FilterPill component that represents a button-like pill with various customizable appearances, shapes, and sizes. Based largely on the `<button>` element.
 *
 * @example
 * ```html
 * <fabric-filter-pill appearance="primary" shape="circular" size="medium" icon-only="false" pressed="false" mixed="false">
 *   <span slot="start">Start Content</span>
 *   Filter Pill Content
 *   <span slot="end">End Content</span>
 * </fabric-filter-pill>
 * ```
 *
 * @attr {FilterPillAppearance} appearance - The appearance of the filter pill.
 * @attr {FilterPillShape} shape - The shape of the filter pill.
 * @attr {FilterPillSize} size - The size of the filter pill.
 * @attr {boolean} icon-only - Whether the filter pill is icon-only.
 * @attr {boolean} pressed - Whether the filter pill is pressed.
 * @attr {boolean} mixed - Whether the filter pill is in a mixed state.
 * @attr {string} aria-pressed - The aria-pressed attribute value.
 * @attr {string} aria-label - The aria-label attribute value.
 *
 * @prop {boolean} iconOnly - Whether the filter pill is icon-only.
 * @prop {boolean} pressed - Whether the filter pill is pressed.
 * @prop {boolean} mixed - Whether the filter pill is in a mixed state.
 *
 * @slot - Default slot for the filter pill content.
 * @slot start - Content which can be provided before the button content.
 * @slot end - Content which can be provided after the button content.
 *
 * @csspart content - The content of the filter pill.
 *
 * @cssprop --icon-spacing - Custom property defined in component styles
 *
 * @method iconOnlyChanged - Handles changes to the iconOnly attribute.
 * @method appearanceChanged - Handles changes to the appearance attribute.
 * @method shapeChanged - Handles changes to the shape attribute.
 * @method sizeChanged - Handles changes to the size attribute.
 * @method pressedChanged - Handles changes to the pressed attribute.
 * @method mixedChanged - Handles changes to the mixed attribute.
 * @method press - Toggles the pressed state of the filter pill.
 *
 * @fires pressed-change - Dispatched when the pressed state changes.
 *
 * @extends Button
 * @tagname fabric-filter-pill
 * @public
 */
export class FilterPill extends Button {
    /**
     * Creates an instance of FilterPill.
     */
    constructor() {
        super();
        /**
         * The appearance of the filter pill.
         * @attr
         * @type {FilterPillAppearance}
         * @public
         */
        this.appearance = FilterPillAppearances.primary;
        /**
         * The shape of the filter pill.
         * @attr
         * @type {FilterPillShape}
         * @public
         */
        this.shape = FilterPillShapes.circular;
        /**
         * The size of the filter pill.
         * @attr
         * @type {FilterPillSize}
         * @public
         */
        this.size = FilterPillSizes.medium;
        /**
         * Whether the filter pill is icon-only.
         * @attr {boolean} icon-only
         * @type {boolean}
         * @public
         */
        this.iconOnly = false;
        /**
         * Whether the filter pill is pressed.
         * @attr {boolean}
         * @type {boolean}
         * @public
         */
        this.pressed = false;
        /**
         * Whether the filter pill is in a mixed state.
         * @attr {boolean}
         * @type {boolean}
         * @public
         */
        this.mixed = false;
        this.elementInternals.role = "button";
    }
    /**
     * Wires click and keypress handlers using a shared AbortSignal.
     */
    attachEventListeners() {
        var _a;
        (_a = this.events) === null || _a === void 0 ? void 0 : _a.abort();
        this.events = new AbortController();
        const { signal } = this.events;
        this.addEventListener("click", this.clickHandler, {
            signal
        });
        this.addEventListener("keypress", this.keypressHandler, {
            signal
        });
    }
    /**
     * Handles changes to the iconOnly attribute.
     * @param {boolean} prev - The previous value of the iconOnly attribute.
     * @param {boolean} next - The new value of the iconOnly attribute.
     */
    iconOnlyChanged(prev, next) {
        toggleState(this.elementInternals, "icon", next);
    }
    /**
     * Handles changes to the appearance attribute.
     * @param {FilterPillAppearance | undefined} prev - The previous value of the appearance attribute.
     * @param {FilterPillAppearance | undefined} next - The new value of the appearance attribute.
     */
    appearanceChanged(prev, next) {
        handleAttributeChange(this.elementInternals, prev, next, toggleState);
    }
    /**
     * Handles changes to the shape attribute.
     * @param {FilterPillShape | undefined} prev - The previous value of the shape attribute.
     * @param {FilterPillShape | undefined} next - The new value of the shape attribute.
     */
    shapeChanged(prev, next) {
        handleAttributeChange(this.elementInternals, prev, next, toggleState);
    }
    /**
     * Handles changes to the size attribute.
     * @param {FilterPillSize | undefined} prev - The previous value of the size attribute.
     * @param {FilterPillSize | undefined} next - The new value of the size attribute.
     */
    sizeChanged(prev, next) {
        handleAttributeChange(this.elementInternals, prev, next, toggleState);
    }
    /**
     * Handles changes to the pressed attribute.
     */
    pressedChanged() {
        this.setPressedState();
    }
    /**
     * Handles changes to the mixed attribute.
     */
    mixedChanged() {
        this.setPressedState();
    }
    /**
     * Toggles the pressed state of the filter pill.
     */
    press() {
        this.pressed = !this.pressed;
    }
    /**
     * Sets the pressed state of the filter pill.
     * @private
     */
    setPressedState() {
        const ariaPressed = `${this.mixed ? "mixed" : !!this.pressed}`;
        this.elementInternals.ariaPressed = ariaPressed;
        toggleState(this.elementInternals, "pressed", this.pressed || this.mixed);
        this.$emit(FilterPillEventNames.pressedChange, this.pressed);
    }
    /**
     * Lifecycle hook to attach event listeners once connected.
     */
    connectedCallback() {
        super.connectedCallback();
        this.attachEventListeners();
    }
    /**
     * Lifecycle hook to clean up event listeners when disconnected.
     */
    disconnectedCallback() {
        var _a;
        (_a = this.events) === null || _a === void 0 ? void 0 : _a.abort();
        this.events = undefined;
        super.disconnectedCallback();
    }
}
__decorate([
    attr
], FilterPill.prototype, "appearance", void 0);
__decorate([
    attr
], FilterPill.prototype, "shape", void 0);
__decorate([
    attr
], FilterPill.prototype, "size", void 0);
__decorate([
    attr({ attribute: "icon-only", mode: "boolean" })
], FilterPill.prototype, "iconOnly", void 0);
__decorate([
    attr({ mode: "boolean" })
], FilterPill.prototype, "pressed", void 0);
__decorate([
    attr({ mode: "boolean" })
], FilterPill.prototype, "mixed", void 0);
//# sourceMappingURL=filter-pill.js.map